import { isActiveCrawlRunStatus } from "../../enums/core";
import type { PipelineStageState, RunStageKey } from "../../enums/run-stage";
import { PIPELINE_STAGE_STATE, RUN_STAGE_KEY } from "../../enums/run-stage";
import type { RunSummary } from "./run-read.responses";

// The four hops a scan actually makes, derived from the run's counts so every
// surface tells the same story. Derived, never stored: the counts are the
// source of truth and a stage is only ever a reading of them.
//
// Deliberately absent: `pending` and `skipped` frontier rows. Nothing writes
// either status today (rows are born `in_flight`), so rendering them would be
// permanent zeros presented as real state.

export type RunStage = {
  key: RunStageKey;
  state: PipelineStageState;
  done: number;
  failed: number;
  // Null for canonicalize, which is one run-level hop with nothing to count.
  total: number | null;
};

function countedState(
  done: number,
  failed: number,
  total: number,
  landed: boolean,
): PipelineStageState {
  // A landed run has no work left anywhere, whatever the counts say. Without
  // this an aborted run — whose outstanding rows are failed, not drained —
  // renders its fetch stage `active` while canonicalize already reads `done`.
  if (landed)
    return total === 0
      ? PIPELINE_STAGE_STATE.WAITING
      : PIPELINE_STAGE_STATE.DONE;
  if (total === 0) return PIPELINE_STAGE_STATE.WAITING;
  return done + failed >= total
    ? PIPELINE_STAGE_STATE.DONE
    : PIPELINE_STAGE_STATE.ACTIVE;
}

/**
 * Ordered stages for a run. Page totals differ per stage on purpose: a page
 * that fails to fetch never reaches analysis, so the analyse stage counts only
 * what was actually dispatched to a sandbox.
 */
export function runStages(summary: RunSummary): RunStage[] {
  const { pageCounts, fetchCounts } = summary;
  const discovered =
    pageCounts.pending +
    pageCounts.inFlight +
    pageCounts.scraped +
    pageCounts.failed +
    pageCounts.skipped;
  const fetchTotal =
    fetchCounts.pending + fetchCounts.ready + fetchCounts.failed;
  const landed = !isActiveCrawlRunStatus(summary.status);
  // Claiming canonicalization requires a drained frontier, so by then crawl
  // is provably finished even though the run has not landed.
  const crawlFinished = landed || summary.canonicalizing;

  return [
    {
      key: RUN_STAGE_KEY.CRAWL,
      // Discovery has no completion signal of its own — a run keeps admitting
      // URLs until the frontier drains — so it reports a running count rather
      // than a total it cannot know in advance.
      state:
        discovered === 0
          ? PIPELINE_STAGE_STATE.WAITING
          : crawlFinished
            ? PIPELINE_STAGE_STATE.DONE
            : PIPELINE_STAGE_STATE.ACTIVE,
      done: discovered,
      failed: 0,
      total: null,
    },
    {
      key: RUN_STAGE_KEY.FETCH,
      state: countedState(
        fetchCounts.ready,
        fetchCounts.failed,
        fetchTotal,
        landed,
      ),
      done: fetchCounts.ready,
      failed: fetchCounts.failed,
      total: fetchTotal,
    },
    {
      key: RUN_STAGE_KEY.ANALYSE,
      state: countedState(
        pageCounts.scraped,
        pageCounts.analysisFailed,
        fetchCounts.analysisDispatched,
        landed,
      ),
      done: pageCounts.scraped,
      failed: pageCounts.analysisFailed,
      total: fetchCounts.analysisDispatched,
    },
    {
      key: RUN_STAGE_KEY.CANONICALIZE,
      state: summary.canonicalizing
        ? PIPELINE_STAGE_STATE.ACTIVE
        : landed
          ? PIPELINE_STAGE_STATE.DONE
          : PIPELINE_STAGE_STATE.WAITING,
      done: landed ? 1 : 0,
      failed: 0,
      total: null,
    },
  ];
}
